import type { VideoPlayerOptions } from './VideoPlayer.data';
import VideoElement from '@/vendor/video-element/lib/VideoElement';

export const placeholderVideoFileUrl: string = '/assets/video/blank.mp4';

export default class FilePlayer {
  private readonly element: HTMLDivElement;
  private readonly options!: VideoPlayerOptions;
  private readonly player: VideoElement;
  private currentTime: number = 0;
  private duration: number = 0;
  private progress: number = 0;
  private videoIsLoadedPromise!: Promise<Array<unknown>>;

  public constructor(element: HTMLDivElement, options: VideoPlayerOptions) {
    this.element = element;
    this.options = options;
    const {
      playsinline,
      loop,
      muted,
      url,
      onPlay,
      onPause,
      onEnded,
      onProgress,
      onCreated,
      onReady,
      requireClickListener,
    } = this.options;

    if (onCreated) onCreated();

    this.player = new VideoElement(this.element, {
      loop,
      muted,
      requireClickListener,
      playsInline: playsinline,

      preloadWithXhr: false,
      onReady: () => {
        if (onReady) {
          onReady();
        }
      },
      onTimeUpdate: (currentTime: number, duration: number) => {
        this.currentTime = currentTime;
        this.duration = duration;
        this.progress = currentTime / duration || 0;

        if (onProgress)
          onProgress({
            seconds: this.currentTime,
            duration: this.duration,
            percent: this.progress,
          });
      },
      onPlaying: (isPlaying: boolean) => {
        if (isPlaying) {
          if (onPlay) onPlay();
        } else if (onPause) {
          onPause();
        }
      },
      onEnded,
    });

    if (url) {
      this.setVideo(url, this.options.playing);
    }
  }

  public async setVideo(url: string | undefined, autoplay = true): Promise<void> {
    if (url) {
      this.videoIsLoadedPromise = this.player.setSrc({
        src: url,
      });
      this.duration = (await this?.player?.getDuration()) || 0;
      this.refreshDuration();

      if (this.options.onReady) this.options.onReady();

      if (autoplay) {
        this.play();
      }
    }
  }

  private refreshDuration() {
    this.player
      ?.getDuration()
      ?.then((duration: number) => {
        if (this?.options.onDuration) {
          this.options.onDuration(duration);
        }
        this.duration = duration;
      })
      .catch((error: unknown) => {
        console.warn(error);
      });
  }

  public async play(): Promise<void> {
    await this.videoIsLoadedPromise;
    this?.player?.play();
  }

  public async pause(): Promise<void> {
    await this.videoIsLoadedPromise;
    this?.player?.pause();
  }

  public setVolume(volume: number): void {
    if (this.player.element) {
      this.player.element.volume = volume;
    }
  }

  public setMuted(muted: boolean): void {
    if (this.player.element) {
      this.player.element.muted = muted;
    }
  }

  public async seekTo(seconds: number): Promise<void> {
    await this.videoIsLoadedPromise;
    this?.player?.seek(seconds);
  }

  public getDuration(): number {
    return this.duration;
  }

  public getCurrentTime(): number {
    return this.currentTime;
  }

  public getInternalPlayer(): VideoElement {
    return this.player;
  }

  public dispose(): void {
    if (this.player) {
      this.player.dispose();
    }
  }
}
